import {
	createOperationDescriptor,
	getRequest,
	Observable,
	type Disposable,
	type GraphQLResponse,
	type GraphQLSingularResponse,
	type IEnvironment,
	type GraphQLTaggedNode,
	type Variables,
} from 'relay-runtime';

const isObject = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

const isUsableList = (connection: unknown): boolean => {
	if (!isObject(connection) || !Array.isArray(connection.edges)) return false;
	const { pageInfo } = connection;
	return (
		isObject(pageInfo) &&
		typeof pageInfo.hasNextPage === 'boolean' &&
		(typeof pageInfo.endCursor === 'string' || pageInfo.endCursor === null) &&
		(!pageInfo.hasNextPage || typeof pageInfo.endCursor === 'string') &&
		connection.edges.every(
			(edge) =>
				edge === null ||
				(isObject(edge) &&
					(typeof edge.cursor === 'string' || edge.cursor === null) &&
					(edge.node === null || (isObject(edge.node) && typeof edge.node.id === 'string'))),
		)
	);
};

/** Buffer refreshes and pages so an unusable response cannot erase the visible connection. */
export const refreshSharedAgentSessions = (
	environment: IEnvironment,
	query: GraphQLTaggedNode,
	variables: Variables,
	onComplete: (error: Error | null) => void,
	shape: 'edges' | 'nodes' = 'edges',
): Disposable => {
	const operation = createOperationDescriptor(getRequest(query), variables, { force: true });
	const source = Observable.create<GraphQLResponse>((sink) => {
		let response: GraphQLSingularResponse | undefined;
		const network = environment
			.getNetwork()
			.execute(operation.request.node.params, operation.request.variables, { force: true })
			.subscribe({
				next: (payload) => {
					if (Array.isArray(payload)) {
						sink.error(new Error('Unexpected batched session response'));
						return;
					}
					const next = payload as GraphQLSingularResponse;
					const errors = 'errors' in next ? next.errors : undefined;
					if ('path' in next || 'label' in next || 'incremental' in next) {
						sink.error(new Error('Incomplete session response'));
						return;
					}
					if (
						response &&
						next.data == null &&
						!errors?.length &&
						next.extensions?.is_final === true
					)
						return;
					const connection = next.data?.agent_sessionQuery;
					// Nullable display relationships may fail independently of a usable list.
					// Status has no such relationships, so retain its strict error handling.
					const usable =
						shape === 'edges'
							? isUsableList(connection)
							: !errors?.length && isObject(connection) && Array.isArray(connection.nodes);
					if (response || !usable) {
						sink.error(
							new Error(
								errors?.map((error) => error.message).join('; ') ||
									'Expected one complete session connection',
							),
						);
						return;
					}
					if (
						shape === 'edges' &&
						variables.after != null &&
						isObject(connection) &&
						isObject(connection.pageInfo) &&
						connection.pageInfo.hasNextPage &&
						connection.pageInfo.endCursor === variables.after
					) {
						sink.error(new Error('AgentSession pagination did not advance its cursor.'));
						return;
					}
					response = next;
				},
				error: (error: Error) => sink.error(error),
				complete: () => {
					if (response) {
						sink.next(response);
						sink.complete();
					} else sink.error(new Error('Missing session response'));
				},
			});
		return () => {
			response = undefined;
			network.unsubscribe();
		};
	});
	const request = environment
		.executeWithSource({ operation, source })
		.subscribe({ complete: () => onComplete(null), error: onComplete });
	return {
		dispose: () => {
			request.unsubscribe();
		},
	};
};
